import * as THREE from 'three';
import type { Work } from '../manifest';
import { layoutCard, type CardLayout } from './cardLayout';

// "Quiet museum, loud art" — the framed-artwork card, baked on Canvas 2D.
// A warm-ivory archival mat lifts off the dark wall on a baked float-shadow;
// the artwork sits in a precisely-cut recessed window with a hairline keyline +
// baked inner contact shadow; an asymmetric mat with a deeper bottom "weight
// line" carries the letter-spaced title + a single palette tick + the year.
// MeshBasicMaterial has no lights, so every bevel/shadow/recess is painted as a
// gradient (light implied from upper-left). Ports the approved frame study 1:1.

const TEX_PPU = 512; // canvas pixels per world unit

const MAT = {
  paperTop: '#f4efe6',   // warm ivory, lit upper-left
  paperBot: '#ece5d8',   // settles cooler/darker toward bottom-right
  windowBase: '#e4ddce', // empty-window recess: a cut, cooler mat
  ink: '#2a2622',        // warm near-black title
  inkMute: '#6f685e',    // muted year
};

function mkTex(canvas: HTMLCanvasElement): THREE.CanvasTexture {
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  tex.needsUpdate = true;
  return tex;
}

/** Pixel-space rects derived from the world-unit layout (origin at canvas top-left). */
interface PxL {
  margin: number; cardW: number; cardH: number;
  matX: number; matY: number; matW: number; matH: number;
  winX: number; winY: number; winW: number; winH: number;
  weightLineY: number; bottom: number; side: number;
}

function pxLayout(layout: CardLayout): PxL {
  const s = TEX_PPU;
  const margin = layout.margin * s;
  const side = layout.side * s;
  const winW = layout.winW * s, winH = layout.winH * s;
  return {
    margin, cardW: layout.cardW * s, cardH: layout.cardH * s,
    matX: margin, matY: margin, matW: layout.matW * s, matH: layout.matH * s,
    winX: margin + side, winY: margin + side, winW, winH,
    weightLineY: margin + side + winH, bottom: layout.bottom * s, side,
  };
}

/* ---- LAYER 1a: baked FLOAT SHADOW in the outer margin (lifts the mat off the
   wall; key light from upper-left → shadow cast down-right). ---- */
function drawFloatShadow(ctx: CanvasRenderingContext2D, L: PxL): void {
  const offX = L.margin * 0.34, offY = L.margin * 0.52;
  ctx.save();
  ctx.shadowColor = 'rgba(0, 0, 0, 0.55)';
  ctx.shadowBlur = L.margin * 0.78;
  ctx.shadowOffsetX = offX;
  ctx.shadowOffsetY = offY;
  ctx.fillStyle = 'rgba(0,0,0,1)';   // only the shadow shows
  ctx.fillRect(L.matX, L.matY, L.matW, L.matH);
  ctx.restore();

  ctx.save();
  ctx.shadowColor = 'rgba(0, 0, 0, 0.40)';
  ctx.shadowBlur = L.margin * 0.28;
  ctx.shadowOffsetX = offX * 0.4;
  ctx.shadowOffsetY = L.margin * 0.18;
  ctx.fillStyle = 'rgba(0,0,0,1)';
  ctx.fillRect(L.matX, L.matY, L.matW, L.matH);
  ctx.restore();
}

/* ---- LAYER 1b: the MAT — warm-ivory gradient + sheen + grain + baked bevel. ---- */
function drawMat(ctx: CanvasRenderingContext2D, L: PxL): void {
  const { matX, matY, matW, matH } = L;

  const g = ctx.createLinearGradient(matX, matY, matX + matW * 0.55, matY + matH);
  g.addColorStop(0, MAT.paperTop);
  g.addColorStop(0.6, '#f0eadf');
  g.addColorStop(1, MAT.paperBot);
  ctx.fillStyle = g;
  ctx.fillRect(matX, matY, matW, matH);

  const sheen = ctx.createRadialGradient(
    matX + matW * 0.28, matY + matH * 0.16, matW * 0.05,
    matX + matW * 0.4, matY + matH * 0.4, matW * 0.95,
  );
  sheen.addColorStop(0, 'rgba(255,253,248,0.5)');
  sheen.addColorStop(0.55, 'rgba(255,253,248,0)');
  ctx.fillStyle = sheen;
  ctx.fillRect(matX, matY, matW, matH);

  drawPaperGrain(ctx, L);

  // baked bevel: warm highlight TOP+LEFT, soft shadow BOTTOM+RIGHT
  ctx.strokeStyle = 'rgba(255, 252, 245, 0.9)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(matX + 0.5, matY + matH);
  ctx.lineTo(matX + 0.5, matY + 0.5);
  ctx.lineTo(matX + matW, matY + 0.5);
  ctx.stroke();

  ctx.strokeStyle = 'rgba(70, 58, 44, 0.32)';
  ctx.beginPath();
  ctx.moveTo(matX + matW - 0.5, matY);
  ctx.lineTo(matX + matW - 0.5, matY + matH - 0.5);
  ctx.lineTo(matX, matY + matH - 0.5);
  ctx.stroke();

  const bevel = ctx.createLinearGradient(
    matX + matW, matY + matH, matX + matW - matW * 0.06, matY + matH - matW * 0.06);
  bevel.addColorStop(0, 'rgba(70,58,44,0.12)');
  bevel.addColorStop(1, 'rgba(70,58,44,0)');
  ctx.fillStyle = bevel;
  ctx.fillRect(matX, matY, matW, matH);
}

/** Warm paper grain — the one place randomness is acceptable (texture, not layout). */
function drawPaperGrain(ctx: CanvasRenderingContext2D, L: PxL): void {
  const { matX, matY, matW, matH } = L;
  const dots = Math.round(matW * matH * 0.012);
  ctx.save();
  for (let i = 0; i < dots; i++) {
    const x = matX + Math.random() * matW;
    const y = matY + Math.random() * matH;
    const a = Math.random() * 0.05;
    ctx.fillStyle = Math.random() < 0.5 ? `rgba(120, 96, 64, ${a})` : `rgba(255, 255, 252, ${a})`;
    ctx.fillRect(x, y, 1, 1);
  }
  ctx.restore();
}

/* ---- LAYER 1c: the WINDOW recess (empty-state aware). Reads as a cut mat even
   before the art loads: cooler base + centre vignette + faint diagonal tooth. ---- */
function drawWindow(ctx: CanvasRenderingContext2D, L: PxL): void {
  const { winX, winY, winW, winH } = L;

  const base = ctx.createLinearGradient(winX, winY, winX, winY + winH);
  base.addColorStop(0, '#ded6c6');
  base.addColorStop(1, MAT.windowBase);
  ctx.fillStyle = base;
  ctx.fillRect(winX, winY, winW, winH);

  const vig = ctx.createRadialGradient(
    winX + winW * 0.5, winY + winH * 0.46, winW * 0.05,
    winX + winW * 0.5, winY + winH * 0.5, Math.max(winW, winH) * 0.62,
  );
  vig.addColorStop(0, 'rgba(255,255,250,0.22)');
  vig.addColorStop(1, 'rgba(120,108,86,0.10)');
  ctx.fillStyle = vig;
  ctx.fillRect(winX, winY, winW, winH);

  ctx.save();
  ctx.beginPath();
  ctx.rect(winX, winY, winW, winH);
  ctx.clip();
  ctx.strokeStyle = 'rgba(120, 104, 80, 0.05)';
  ctx.lineWidth = 1;
  const step = Math.max(7, winW * 0.018);
  for (let d = -winH; d < winW; d += step) {
    ctx.beginPath();
    ctx.moveTo(winX + d, winY);
    ctx.lineTo(winX + d + winH, winY + winH);
    ctx.stroke();
  }
  ctx.restore();
}

/* ---- LAYER 2: the WINDOW MOUNT RING (its own transparent overlay plane, drawn
   on TOP of the art). Crisp keyline + baked inner contact shadow so the art reads
   recessed (top/left darker, bottom/right faint highlight). Accent = 1px top line.
   Drawn in the ring canvas's own space: origin (0,0), full canvas = window. ---- */
function drawMountRing(ctx: CanvasRenderingContext2D, w: number, h: number, accent: string): void {
  const depth = Math.max(6, w * 0.022);

  ctx.save();
  ctx.beginPath();
  ctx.rect(0, 0, w, h);
  ctx.clip();

  let s = ctx.createLinearGradient(0, 0, 0, depth);
  s.addColorStop(0, 'rgba(20, 16, 10, 0.42)');
  s.addColorStop(1, 'rgba(20, 16, 10, 0)');
  ctx.fillStyle = s;
  ctx.fillRect(0, 0, w, depth);

  s = ctx.createLinearGradient(0, 0, depth, 0);
  s.addColorStop(0, 'rgba(20, 16, 10, 0.34)');
  s.addColorStop(1, 'rgba(20, 16, 10, 0)');
  ctx.fillStyle = s;
  ctx.fillRect(0, 0, depth, h);

  s = ctx.createLinearGradient(0, h, 0, h - depth * 0.8);
  s.addColorStop(0, 'rgba(255, 252, 244, 0.16)');
  s.addColorStop(1, 'rgba(255, 252, 244, 0)');
  ctx.fillStyle = s;
  ctx.fillRect(0, h - depth * 0.8, w, depth * 0.8);

  s = ctx.createLinearGradient(w, 0, w - depth * 0.8, 0);
  s.addColorStop(0, 'rgba(255, 252, 244, 0.12)');
  s.addColorStop(1, 'rgba(255, 252, 244, 0)');
  ctx.fillStyle = s;
  ctx.fillRect(w - depth * 0.8, 0, depth * 0.8, h);
  ctx.restore();

  // hairline keyline (the precise cut edge) on all four sides
  ctx.strokeStyle = 'rgba(42, 36, 28, 0.55)';
  ctx.lineWidth = 1;
  ctx.strokeRect(0.5, 0.5, w - 1, h - 1);

  // accent: a single 1px line along the TOP keyline only — the one chromatic touch
  ctx.strokeStyle = accent;
  ctx.globalAlpha = 0.9;
  ctx.beginPath();
  ctx.moveTo(0.5, 0.5);
  ctx.lineTo(w - 0.5, 0.5);
  ctx.stroke();
  ctx.globalAlpha = 1;
}

/* ---- WEIGHT LINE: bottom-margin metadata — tracked uppercase TITLE (left), a
   short accent TICK, muted mono YEAR (right). ---- */
function drawWeightLine(
  ctx: CanvasRenderingContext2D, L: PxL, title: string, year: number, accent: string,
): void {
  const baselineY = L.weightLineY + L.bottom * 0.5;
  const titleSize = Math.max(11, Math.round(L.matW * 0.034));
  const yearSize = Math.round(titleSize * 0.72);
  const tracking = 0.12;

  const leftX = L.matX + L.side;
  const rightX = L.matX + L.matW - L.side;

  ctx.textBaseline = 'middle';

  // YEAR (right, mono, muted)
  ctx.font = `400 ${yearSize}px ui-monospace, "SF Mono", monospace`;
  ctx.fillStyle = MAT.inkMute;
  const yearLabel = String(year);
  drawTracked(ctx, yearLabel, rightX, baselineY, 0.04, 'right');
  const yearW = measureTracked(ctx, yearLabel, 0.04);

  // accent TICK just left of the year
  const tickW = Math.max(14, L.matW * 0.05);
  const gap = titleSize * 0.9;
  const tickX2 = rightX - yearW - gap;
  const tickX1 = tickX2 - tickW;
  ctx.strokeStyle = accent;
  ctx.lineWidth = Math.max(1, Math.round(titleSize * 0.09));
  ctx.beginPath();
  ctx.moveTo(tickX1, baselineY + 0.5);
  ctx.lineTo(tickX2, baselineY + 0.5);
  ctx.stroke();

  // TITLE (left, uppercase, tracked), clamped before the tick
  ctx.font = `600 ${titleSize}px ui-sans-serif, system-ui, sans-serif`;
  ctx.fillStyle = MAT.ink;
  const maxTitleW = (tickX1 - titleSize * 0.7) - leftX;
  const label = title && title.trim() ? title.trim().toUpperCase() : 'UNTITLED';
  drawTracked(ctx, clampTracked(ctx, label, maxTitleW, tracking), leftX, baselineY, tracking, 'left');
}

/* ---- tracked-text helpers (letter-spacing isn't native to fillText) ---- */
/** Parse the px size out of a CSS font string ("600 12px …" → 12), not the weight. */
function fontPxOf(font: string): number {
  const m = /(\d+(?:\.\d+)?)px/.exec(font);
  return m ? parseFloat(m[1]) : 16;
}
function measureTracked(ctx: CanvasRenderingContext2D, str: string, em: number): number {
  const extra = fontPxOf(ctx.font) * em;
  let w = 0;
  for (const ch of str) w += ctx.measureText(ch).width + extra;
  return w - extra;
}
function drawTracked(
  ctx: CanvasRenderingContext2D, str: string, x: number, y: number, em: number, align: 'left' | 'right',
): void {
  const extra = fontPxOf(ctx.font) * em;
  let cx = align === 'right' ? x - measureTracked(ctx, str, em) : x;
  ctx.textAlign = 'left';
  for (const ch of str) {
    ctx.fillText(ch, cx, y);
    cx += ctx.measureText(ch).width + extra;
  }
}
function clampTracked(ctx: CanvasRenderingContext2D, str: string, maxW: number, em: number): string {
  if (measureTracked(ctx, str, em) <= maxW) return str;
  let s = str;
  while (s.length > 1 && measureTracked(ctx, s + '…', em) > maxW) s = s.slice(0, -1);
  return s.trimEnd() + '…';
}

function accentOf(work: Work): string {
  return work.palette[0] ?? '#9a8f80';
}

/** Outer plane: baked float shadow (transparent margin) + mat + empty-window
 *  recess + the weight-line metadata. The art image plane covers the recess
 *  once loaded; the ring overlay frames it. */
export function outerFrameTexture(work: Work, title: string): THREE.CanvasTexture {
  const L = pxLayout(layoutCard(work.aspect));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(L.cardW);
  canvas.height = Math.round(L.cardH);
  const ctx = canvas.getContext('2d')!;
  drawFloatShadow(ctx, L);
  drawMat(ctx, L);
  drawWindow(ctx, L);
  drawWeightLine(ctx, L, title, work.year, accentOf(work));
  return mkTex(canvas);
}

/** Ring overlay plane: keyline + baked inner contact shadow + accent top line.
 *  Sized to the window; centre is transparent so the art shows through. */
export function ringTexture(work: Work): THREE.CanvasTexture {
  const layout = layoutCard(work.aspect);
  const w = Math.round(layout.winW * TEX_PPU);
  const h = Math.round(layout.winH * TEX_PPU);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d')!;
  drawMountRing(ctx, w, h, accentOf(work));
  return mkTex(canvas);
}
